/**
 * Firestore Helper Functions
 * 
 * Provides CRUD operations for Firestore collections.
 * Used for static data (Users, Courses, Templates, Stats).
 */

import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  writeBatch
} from 'firebase/firestore';
import { firestore } from './config';
import { Logger } from '../utils/logger';

const log = Logger.create('Firestore');

// ============================================
// USERS COLLECTION
// ============================================

export const getUserProfile = async (uid) => {
  try {
    const docRef = doc(firestore, 'users', uid);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  } catch (error) {
    log.error('Error getting user profile:', error);
    throw error;
  }
};

export const createUserProfile = async (uid, data) => {
  try {
    const docRef = doc(firestore, 'users', uid);
    await setDoc(docRef, {
      ...data,
      createdAt: Date.now(),
      lastLogin: Date.now()
    });
  } catch (error) {
    log.error('Error creating user profile:', error);
    throw error;
  }
};

export const updateUserProfile = async (uid, data) => {
  try {
    const docRef = doc(firestore, 'users', uid);
    await updateDoc(docRef, {
      ...data,
      lastModified: Date.now()
    });
  } catch (error) {
    log.error('Error updating user profile:', error);
    throw error;
  }
};

export const getAllUsers = async () => {
  try {
    const q = query(collection(firestore, 'users'), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    log.error('Error getting all users:', error);
    throw error;
  }
};

// ============================================
// COURSES COLLECTION
// ============================================

export const getCourses = async (userId) => {
  try {
    const q = query(
      collection(firestore, 'courses'),
      where('owner', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    log.error('Error getting courses:', error);
    throw error;
  }
};

export const getAllCourses = async () => {
  try {
    const q = query(collection(firestore, 'courses'), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    log.error('Error getting all courses:', error);
    throw error;
  }
};

export const getCourse = async (courseId) => {
  try {
    const docRef = doc(firestore, 'courses', courseId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  } catch (error) {
    log.error('Error getting course:', error);
    throw error;
  }
};

export const createCourse = async (data) => {
  try {
    const docRef = doc(collection(firestore, 'courses'));
    await setDoc(docRef, {
      ...data,
      createdAt: Date.now(),
      updatedAt: Date.now()
    });
    return docRef.id;
  } catch (error) {
    log.error('Error creating course:', error);
    throw error;
  }
};

export const updateCourse = async (courseId, data) => {
  try {
    const docRef = doc(firestore, 'courses', courseId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: Date.now()
    });
  } catch (error) {
    log.error('Error updating course:', error);
    throw error;
  }
};

export const deleteCourse = async (courseId) => {
  try {
    const docRef = doc(firestore, 'courses', courseId);
    await deleteDoc(docRef);
  } catch (error) {
    log.error('Error deleting course:', error);
    throw error;
  }
};

// ============================================
// COURSE INVITES COLLECTION
// ============================================

export const getInviteByCode = async (code) => {
  try {
    const docRef = doc(firestore, 'course_invites', code.toUpperCase());
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  } catch (error) {
    log.error('Error getting invite:', error);
    throw error;
  }
};

export const createInvite = async (code, data) => {
  try {
    const docRef = doc(firestore, 'course_invites', code.toUpperCase());
    await setDoc(docRef, {
      ...data,
      code: code.toUpperCase(),
      createdAt: Date.now()
    });
  } catch (error) {
    log.error('Error creating invite:', error);
    throw error;
  }
};

export const updateInvite = async (code, data) => {
  try {
    const docRef = doc(firestore, 'course_invites', code.toUpperCase());
    await updateDoc(docRef, data);
  } catch (error) {
    log.error('Error updating invite:', error);
    throw error;
  }
};

export const deleteInvite = async (code) => {
  try {
    const docRef = doc(firestore, 'course_invites', code.toUpperCase());
    await deleteDoc(docRef);
  } catch (error) {
    log.error('Error deleting invite:', error);
    throw error;
  }
};

export const getCourseInvites = async (courseId) => {
  try {
    const q = query(
      collection(firestore, 'course_invites'),
      where('courseId', '==', courseId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    log.error('Error getting course invites:', error);
    throw error;
  }
};

// ============================================
// QUIZ TEMPLATES COLLECTION
// ============================================

export const getTemplates = async (userId) => {
  try {
    const q = query(
      collection(firestore, 'quiz_templates'),
      where('userId', '==', userId),
      orderBy('lastUsed', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    log.error('Error getting templates:', error);
    throw error;
  }
};

export const getTemplate = async (templateId) => {
  try {
    const docRef = doc(firestore, 'quiz_templates', templateId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  } catch (error) {
    log.error('Error getting template:', error);
    throw error;
  }
};

export const createTemplate = async (data) => {
  try {
    const docRef = doc(collection(firestore, 'quiz_templates'));
    await setDoc(docRef, {
      ...data,
      createdAt: Date.now(),
      lastUsed: Date.now(),
      useCount: 0
    });
    return docRef.id;
  } catch (error) {
    log.error('Error creating template:', error);
    throw error;
  }
};

export const updateTemplate = async (templateId, data) => {
  try {
    const docRef = doc(firestore, 'quiz_templates', templateId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: Date.now()
    });
  } catch (error) {
    log.error('Error updating template:', error);
    throw error;
  }
};

export const deleteTemplate = async (templateId) => {
  try {
    const docRef = doc(firestore, 'quiz_templates', templateId);
    await deleteDoc(docRef);
  } catch (error) {
    log.error('Error deleting template:', error);
    throw error;
  }
};

// ============================================
// QUIZ INSTANCES COLLECTION
// ============================================

export const getInstances = async (userId) => {
  try {
    const q = query(
      collection(firestore, 'quiz_instances'),
      where('owner', '==', userId),
      orderBy('startedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    log.error('Error getting instances:', error);
    throw error;
  }
};

export const getAllInstances = async () => {
  try {
    const snapshot = await getDocs(collection(firestore, 'quiz_instances'));
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    log.error('Error getting all instances:', error);
    throw error;
  }
};

export const getInstance = async (instanceId) => {
  try {
    const docRef = doc(firestore, 'quiz_instances', instanceId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  } catch (error) {
    log.error('Error getting instance:', error);
    throw error;
  }
};

export const createInstance = async (data) => {
  try {
    const docRef = doc(collection(firestore, 'quiz_instances'));
    await setDoc(docRef, {
      ...data,
      startedAt: Date.now()
    });
    return docRef.id;
  } catch (error) {
    log.error('Error creating instance:', error);
    throw error;
  }
};

export const updateInstance = async (instanceId, data) => {
  try {
    const docRef = doc(firestore, 'quiz_instances', instanceId);
    await updateDoc(docRef, data);
  } catch (error) {
    log.error('Error updating instance:', error);
    throw error;
  }
};

export const deleteInstance = async (instanceId) => {
  try {
    const docRef = doc(firestore, 'quiz_instances', instanceId);
    await deleteDoc(docRef);
  } catch (error) {
    log.error('Error deleting instance:', error);
    throw error;
  }
};

// ============================================
// STUDENT STATS COLLECTION
// ============================================

export const getStudentStats = async (studentId) => {
  try {
    const docRef = doc(firestore, 'student_stats', studentId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  } catch (error) {
    log.error('Error getting student stats:', error);
    throw error;
  }
};

export const updateStudentStats = async (studentId, data) => {
  try {
    const docRef = doc(firestore, 'student_stats', studentId);
    await updateDoc(docRef, data);
  } catch (error) {
    log.error('Error updating student stats:', error);
    throw error;
  }
};

export const createStudentStats = async (studentId, data) => {
  try {
    const docRef = doc(firestore, 'student_stats', studentId);
    await setDoc(docRef, {
      ...data,
      createdAt: Date.now()
    });
  } catch (error) {
    log.error('Error creating student stats:', error);
    throw error;
  }
};

export const setStudentStats = async (studentId, data) => {
  try {
    const docRef = doc(firestore, 'student_stats', studentId);
    await setDoc(docRef, {
      ...data,
      updatedAt: Date.now()
    }, { merge: true });
  } catch (error) {
    log.error('Error setting student stats:', error);
    throw error;
  }
};

// Quiz History sub-collection
export const addQuizHistory = async (studentId, sessionId, data) => {
  try {
    const docRef = doc(firestore, 'student_stats', studentId, 'quizHistory', sessionId);
    await setDoc(docRef, {
      ...data,
      completedAt: Date.now()
    });
  } catch (error) {
    log.error('Error adding quiz history:', error);
    throw error;
  }
};

export const getQuizHistory = async (studentId, courseId = null) => {
  try {
    const collectionRef = collection(firestore, 'student_stats', studentId, 'quizHistory');
    let q = query(collectionRef, orderBy('completedAt', 'desc'));
    
    if (courseId) {
      q = query(collectionRef, where('courseId', '==', courseId), orderBy('completedAt', 'desc'));
    }
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    log.error('Error getting quiz history:', error);
    throw error;
  }
};

// Recent Activity
export const addRecentActivity = async (studentId, activity) => {
  try {
    const statsRef = doc(firestore, 'student_stats', studentId);
    const statsSnap = await getDoc(statsRef);
    
    if (statsSnap.exists()) {
      const currentData = statsSnap.data();
      let activities = currentData.recentActivity || [];
      
      // Add new activity at the beginning
      activities.unshift(activity);
      
      // Keep only the last 50 activities
      if (activities.length > 50) {
        activities = activities.slice(0, 50);
      }
      
      await updateDoc(statsRef, { recentActivity: activities });
    }
  } catch (error) {
    log.error('Error adding recent activity:', error);
    throw error;
  }
};

// Course Stats
export const getCourseStats = async (studentId, courseId) => {
  try {
    const statsRef = doc(firestore, 'student_stats', studentId);
    const statsSnap = await getDoc(statsRef);
    
    if (statsSnap.exists()) {
      const data = statsSnap.data();
      return data.courseStats?.[courseId] || null;
    }
    return null;
  } catch (error) {
    log.error('Error getting course stats:', error);
    throw error;
  }
};

export const updateCourseStats = async (studentId, courseId, courseStats) => {
  try {
    const statsRef = doc(firestore, 'student_stats', studentId);
    await updateDoc(statsRef, {
      [`courseStats.${courseId}`]: {
        ...courseStats,
        updatedAt: Date.now()
      }
    });
  } catch (error) {
    log.error('Error updating course stats:', error);
    throw error;
  }
};

// ============================================
// BATCH OPERATIONS
// ============================================

export const batchWrite = async (operations) => {
  try {
    const batch = writeBatch(firestore);
    
    for (const op of operations) {
      const docRef = doc(firestore, op.collection, op.id);
      
      if (op.type === 'set') {
        batch.set(docRef, op.data);
      } else if (op.type === 'update') {
        batch.update(docRef, op.data);
      } else if (op.type === 'delete') {
        batch.delete(docRef);
      }
    }
    
    await batch.commit();
  } catch (error) {
    log.error('Error in batch write:', error);
    throw error;
  }
};
